import type { Event, EventCallback } from '@tauri-apps/api/event'
import { useRenderNotifier } from '@wui/common-library'
import { SubscriberStatus, useIM } from '@wui/im'

import { useStoreSettingsNotifications } from '@/app/store/pinia/settings/notifications'
import { useSetStatusHook } from '@/composables/useSetStatusHook'

import { useLogoutHook } from '@/features/logout'

import { useStoreContacts } from '@/entities/contact'
import { useChatMessageStore } from '@/entities/messages/chat/model'

import { useLocalization } from '@/shared/composables/localization'
import { resolveManualStatusChange } from '@/shared/lib/resolve-manual-status-change'
import { SystemTrayEvent } from '@/shared/types/tauri'

import { tauriMode } from '../import-meta'

const handlerEventQuitProfile = () => {
  useLogoutHook().logout()
  updateStatusIcon(0)
}

const setStatus = async (status: SubscriberStatus, cb?: () => void) => {
  const im = useIM()
  const { setUpdateDNDonSetStatus } = useSetStatusHook()
  const { t } = useLocalization()
  const { notifyError } = useRenderNotifier()
  const {
    callStatusDoNotDisturb,
    setCallStatusDoNotDisturbIsChangedByCall,
  } = useStoreSettingsNotifications()
  const { payload, shouldClearChangedByCall } = resolveManualStatusChange(
    status,
    callStatusDoNotDisturb,
  )

  return im
    .setSubscriberStatus(payload)
    .then((result) => {
      if (result) {
        setUpdateDNDonSetStatus(status)
        if (shouldClearChangedByCall) {
          setCallStatusDoNotDisturbIsChangedByCall(false)
        }
        if (cb) {
          cb()
        }
      }
    })
    .catch((e: unknown) => {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
    })
}

const handlerEventStatusUser = (status: string) => {
  if (status === SystemTrayEvent.STATUS_ONLINE) {
    setStatus(SubscriberStatus.ONLINE)
  }
  if (status === SystemTrayEvent.STATUS_ABSENT) {
    setStatus(SubscriberStatus.ABSENT)
  }
  if (status === SystemTrayEvent.STATUS_NOT_DISTURB) {
    setStatus(SubscriberStatus.DO_NOT_DISTURB)
  }
}

export const handlerSystemTray: EventCallback<{ evet_name: string }> = (event: Event<{ evet_name: string }>) => {
  if (event.payload.evet_name === SystemTrayEvent.QUIT_PROFILE) {
    handlerEventQuitProfile()
  } else if (
    event.payload.evet_name === SystemTrayEvent.STATUS_ABSENT ||
    event.payload.evet_name === SystemTrayEvent.STATUS_NOT_DISTURB ||
    event.payload.evet_name === SystemTrayEvent.STATUS_ONLINE
  ) {
    handlerEventStatusUser(event.payload.evet_name)
  }
}

export const updateStatusIcon = (status?: number) => {
  if (status === undefined) {
    const { ownStatus } = useStoreContacts()
    const { totalUnreadCount } = useChatMessageStore()
    status = ownStatus + (totalUnreadCount > 0 ? 3 : 0)
  }
  if (tauriMode()) {

    switch (status) {
    case 1:
      return invokeUpdateStatusWithCurrentWindow('online')
    case 2:
      return invokeUpdateStatusWithCurrentWindow('absent')
    case 3:
      return invokeUpdateStatusWithCurrentWindow('not_disturb')
    case 4:
      return invokeUpdateStatusWithCurrentWindow('unread_online')
    case 5:
      return invokeUpdateStatusWithCurrentWindow('unread_absent')
    case 6:
      return invokeUpdateStatusWithCurrentWindow('unread_not_disturb')
    default:
      return invokeUpdateStatusWithCurrentWindow('offline')
    }
  } else {
    return status
  }
}

const invokeUpdateStatusWithCurrentWindow = async <T = unknown>(status: string): Promise<T> => {
  const { invoke } = await import('@tauri-apps/api/core')
  const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow')
  return invoke('update_status', {
    window: getCurrentWebviewWindow(),
    status,
  })
}
